// Imports
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components/macro';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import keyValueStorage from '../../../shareables/foundation/front-end/utils/key-value-storage';
import theme from '../../../shareables/theme';
import notificationBoxItems from './notification-box-items';


// Styled components
const Container = styled.button<{ opened: boolean }>`
	grid-column: 3;
	
	background: none;
	border: none;
	color: ${(props) => (props.theme.isPaper ? '#000' : '#fff')};
	
	margin-right: 0.625rem;
	margin-left: 0.375rem;
	
	height: 100%;
	position: relative;
	font-family: inherit;
	font-size: 0.8125rem;
	
	${(props) =>
		props.opened &&
		css`
			background: #fff;
			color: #000;
		`}
`;

const flash = keyframes`
	0% {
		opacity: 1;
	}
	
	50% {
		opacity: 0.01;
	}
	
	100% {
		opacity: 1;
	}
`;

const Badge = styled.span<{ shouldFlash: boolean }>`
	width: 1rem;
	height: 1rem;
	background: ${theme.colors.danger};
	border-radius: 50%;
	font-size: 0.6875rem;
	line-height: 1rem;
	text-align: center;
	color: #fff;
	
	position: absolute;
	margin-left: -0.1875rem;
	top: 0.125rem;
	
	${(props) =>
		props.shouldFlash &&
		css`
			animation: ${flash} 0.4s linear 1s 6;
		`}
`;

const DropdownMenu = styled.div`
	min-width: 15.625rem;
	padding: 0.3125rem 0.9375rem;
	background: #fff;
	border-bottom-left-radius: 0.1875rem;
	border-bottom-right-radius: 0.1875rem;
	line-height: 1.375rem;
	text-align: right;
	color: #000;
	box-shadow: 0 0.5rem 0.5rem rgba(0, 0, 0, 0.25);
	position: absolute;
	cursor: default;
	z-index: 99;
	right: 0;
	
	top: 100%;
	
	ul {
		list-style-type: none;
		
		li a {
			color: #000;
		}
	}
`;


// Define the accepted props
interface Props {
	storybookIsNotificationDropdownOpen?: true;
}


// Function component
const NotificationBox: React.FC<Props> = ({ storybookIsNotificationDropdownOpen }) => {
	// Use Redux functionality
	const member = useAppSelector((state) => state.self.member);
	
	
	// Get array of drop down items
	const items = member ? notificationBoxItems(member) : [];
	
	
	// Use accessible dropdown
	const { buttonProps, itemProps, isOpen } = useDropdownMenu(items.length > 0 ? items.length : 1);
	
	
	// Initialize
	const dropdownMenuList: ReactNode[] = [];
	
	
	// Generate drop down menu JSX
	if (items.length === 0) {
		// Handle no notifications
		dropdownMenuList.push(<em>No notifications</em>);
	} else {
		// Iterate over items
		for (const [index, item] of items.entries()) {
			dropdownMenuList.push(
				<li key={item.uniqueKey}>
					<Link {...itemProps[index]} to={item.to}>
						{item.content}
					</Link>
				</li>
			);
		}
	}
	
	
	// Determine if the notification badge should flash
	const shouldBadgeFlash = (() => {
		// Attempt to get expiration time
		const expiresAt = keyValueStorage.getItem('alert_blink_expiration');
		
		
		// Return if it doesn't exist
		if (!expiresAt) {
			return true;
		}
		
		
		// Compare if we're past the time
		return Date.now() > Number(expiresAt);
	})();
	
	
	// Handle animation end
	const handleAnimationEnd = () => {
		// Set new expiration time
		const newExpiration = Date.now() + 1000 * 60 * 30;
		keyValueStorage.setItem('alert_blink_expiration', String(newExpiration));
	};
	
	
	// Return JSX
	return (
		<Container
			{...buttonProps}
			aria-label='Notification dropdown menu'
			opened={storybookIsNotificationDropdownOpen ?? isOpen}
		>
			<FontAwesomeIcon icon={faBell} />
			
			{((member?.notifications?.availablePaymentOptions ?? 0) > 0 ||
				(member?.notifications?.availableSurveys ?? 0) > 0) && (
				<Badge shouldFlash={shouldBadgeFlash} onAnimationEnd={handleAnimationEnd}>
					{(member?.notifications?.availablePaymentOptions ?? 0) + (member?.notifications?.availableSurveys ?? 0)}
				</Badge>
			)}
			
			{(storybookIsNotificationDropdownOpen ?? isOpen) && (
				<DropdownMenu role='menu'>
					<ul>{dropdownMenuList}</ul>
				</DropdownMenu>
			)}
		</Container>
	);
};

export default NotificationBox;
